import { Component } from '@theme/component';
import { sectionRenderer, morphSection } from '@theme/section-renderer';

/** Homepage collections have no facets. Read this region once in its collection context. */
class AtelierUseLinks extends Component {
  #observer;
  #events;
  #request;

  connectedCallback() {
    super.connectedCallback();
    this.#events = new AbortController();
    this.addEventListener('click', event => {
      if (event.target.closest('[data-finder-retry]')) this.load();
    }, { signal: this.#events.signal });
    if (this.querySelector('[data-finder-state]')?.dataset.finderState !== 'pending') return;
    if ('IntersectionObserver' in window && !window.Shopify?.designMode) {
      this.#observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          this.#observer.disconnect();
          this.load();
        }
      }, { rootMargin: '800px 0px', threshold: 0 });
      this.#observer.observe(this);
    } else this.load();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#events?.abort();
    this.#request?.abort();
    this.#request = null;
  }

  async load() {
    const content = this.querySelector('[data-hydration-key="atelier-finder-links"]');
    if (this.#request || !this.isConnected || content?.dataset.finderState === 'ready') return;
    const feedback = this.querySelector('[data-finder-feedback]');
    const status = this.querySelector('[data-finder-status]');
    const retry = this.querySelector('[data-finder-retry]');
    const controller = new AbortController();
    this.#request = controller;
    this.setAttribute('aria-busy', 'true');
    feedback.hidden = false;
    status.hidden = false;
    status.textContent = this.dataset.loading;
    retry.hidden = true;
    const timeout = setTimeout(() => controller.abort(), 12000);
    try {
      const url = new URL(this.dataset.catalogUrl, window.location.href);
      if (url.origin !== window.location.origin) throw new Error('Finder collection origin mismatch');
      const html = await sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url, controller.signal);
      if (controller.signal.aborted || this.#request !== controller || !this.isConnected) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const section = doc.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const next = section?.querySelector('[data-hydration-key="atelier-finder-links"][data-finder-state="ready"]');
      if (!next?.querySelector('[data-atelier-taxonomy-group]')) throw new Error('Finder facet response is unavailable');
      await morphSection(this.dataset.sectionId, html, { mode: 'hydration' });
      if (this.#request !== controller || !this.isConnected) return;
      if (this.querySelector('[data-finder-state]')?.dataset.finderState !== 'ready') throw new Error('Finder hydration did not complete');
      if (feedback.contains(document.activeElement)) {
        this.querySelector('[data-hydration-key="atelier-finder-links"] a')?.focus();
      }
      this.querySelector('[data-finder-feedback]').hidden = true;
    } catch {
      if (this.#request !== controller || !this.isConnected) return;
      status.textContent = this.dataset.failed;
      retry.hidden = false;
    } finally {
      clearTimeout(timeout);
      if (this.#request === controller) {
        this.#request = null;
        this.removeAttribute('aria-busy');
      }
    }
  }
}

if (!customElements.get('fivepoint-atelier-use-links')) {
  customElements.define('fivepoint-atelier-use-links', AtelierUseLinks);
}
